import {
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  Timestamp,
} from 'firebase/firestore';
import type { BlockLibraryExampleDoc } from '@modules/shared-types';

function toTimestamp(value: Date | string | null | undefined): Timestamp | null | undefined {
  if (value === null) return null;
  if (value === undefined) return undefined;
  return Timestamp.fromDate(value instanceof Date ? value : new Date(value));
}

function toDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  return undefined;
}

export const blockLibraryConverter: FirestoreDataConverter<BlockLibraryExampleDoc> = {
  toFirestore(value: BlockLibraryExampleDoc) {
    const { id: _id, ...data } = value;
    return {
      ...data,
      // Store both potentially deep/heterogeneous representations as JSON
      // strings. This avoids Firestore reserved/nested undefined keys and makes
      // the dual payload byte-for-byte portable across Web/Admin SDKs.
      blueprint: JSON.stringify(data.blueprint),
      previewScreens: JSON.stringify(data.previewScreens),
      createdAt: toTimestamp(data.createdAt),
      updatedAt: toTimestamp(data.updatedAt),
    };
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): BlockLibraryExampleDoc {
    const data = snapshot.data(options);
    if (typeof data.blueprint !== 'string' || typeof data.previewScreens !== 'string') {
      throw new Error(`Block library record ${snapshot.id} is not a strict v2 JSON-string document.`);
    }
    return {
      ...data,
      id: snapshot.id,
      blueprint: JSON.parse(data.blueprint),
      previewScreens: JSON.parse(data.previewScreens),
      createdAt: toDate(data.createdAt) ?? new Date(0),
      updatedAt: toDate(data.updatedAt),
    } as BlockLibraryExampleDoc;
  },
};
